import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    Signal,
    signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';

import { FieldInterventionFormListBloc } from '@connect-services/client/blocs/field-intervention-form-list.bloc';
import { AbstractPaginatedComponent } from '@connect-services/client/components/abstract-paginated-component/abstract-paginated.component';
import { ManagementListHeaderComponent } from '@connect-services/client/components/management/management-list-header/management-list-header.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import { CustomColumn, CustomTableComponent } from '@connect-services/src/lib/components/table/table.component';
import { FieldInterventionFormControllerGet200Response } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { defaultPaginationParameters, OrderEnum } from '@shared/client/models/pagination.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CustomTableComponent,
        ManagementListHeaderComponent,
        NoDataToDisplayComponent,
        PaginatorComponent,
        RessourcesModule,
    ],
    selector: 'app-forms-list',
    templateUrl: './forms-list.component.html',
})
export class FormsListComponent extends AbstractPaginatedComponent {
    public readonly formsListBloc = inject(FieldInterventionFormListBloc);
    public readonly paginatedForms: WritableSignal<FieldInterventionFormControllerGet200Response | null> = signal(null);
    public override readonly paginator: Signal<PaginatorComponent | undefined> =
        viewChild<PaginatorComponent>('paginator');
    public readonly router = inject(Router);
    public readonly totalItems: Signal<number> = computed((): number => this.paginatedForms()?.totalItems ?? 0);

    protected tableColumns: CustomColumn[] = [
        {
            key: 'name',
            label: 'formName',
        },
        {
            key: 'description',
            label: 'description',
        },
        {
            key: 'countControls',
            label: 'countControls',
            sortable: false,
        },
        {
            key: 'createdAt',
            label: 'createdAt',
        },
    ];
    protected readonly translateService = inject(TranslateService);

    private readonly searchValue: WritableSignal<string> = signal('');

    public constructor() {
        super();
        this.paginationParameters = {
            ...defaultPaginationParameters,
            order: OrderEnum.ASC,
            orderBy: 'name',
            take: 10,
        };

        effect(() => {
            if (!this.formsListBloc.value()) {
                return;
            }

            this.paginatedForms.set(this.formsListBloc.value());
        });
    }

    public override async loadBloc(): Promise<void> {
        await this.formsListBloc.load(
            {
                ...this.paginationParameters,
                ...(this.searchValue() && { properties: ['name', 'description'] }),
                value: this.searchValue(),
            },
            { refresh: true },
        );
    }

    public async openFormDetails(id: string): Promise<void> {
        await this.router.navigate([RoutePathEnum.MANAGEMENT_DATA, RoutePathEnum.FORMS, id]);
    }

    public async searchForm(searchValue: string): Promise<void> {
        this.searchValue.set(searchValue);
        this.paginator()?.resetPaginator();
        await this.loadBloc();
    }
}
